import type { OfferStatus } from './api/partner-offers.api';

/**
 * Badge tones live in `lib/constants/offer-status.ts`, shared with the admin
 * offers console - the same offer must not be a different colour on the two
 * screens. Re-exported here so this console's own call sites keep importing
 * their status lookups from one place.
 */
export { OFFER_STATUS_BADGE_VARIANT } from '@/lib/constants/offer-status';

/**
 * Labels are NOT shared with the admin console, deliberately: the partner
 * reads "Submitted" because they submitted it, the admin reads "Awaiting
 * review" because it is waiting on them. One lookup per status either way,
 * never a repeated if/switch on `status` at each call site (mnr-standards).
 */
export const OFFER_STATUS_LABEL: Record<OfferStatus, string> = {
  draft: 'Draft',
  submitted: 'Submitted',
  accepted: 'Accepted',
  rejected: 'Rejected',
  live: 'Live',
  inactive: 'Inactive',
};

export const PARTNER_OFFERS_STRINGS = {
  PAGE_TITLE: 'Perks & Offers',
  PAGE_DESCRIPTION:
    'Create and manage your special offers for My NEXT Raise members. Accepted offers are published to the Perks Page after MNR approval.',
  CREATE_NEW_OFFER: '+ Create New Offer',
  STAT_TOTAL: 'Total Offers Created',
  STAT_AWAITING: 'Awaiting MNR Decision',
  STAT_ACCEPTED: 'Accepted by MNR',
  STAT_LIVE: 'Live on Perks Page',
  SECTION_AWAITING_TITLE: 'Awaiting MNR Decision',
  SECTION_AWAITING_DESCRIPTION: 'These offers have been submitted to the MNR Communications team and are pending review.',
  SECTION_ACCEPTED_TITLE: 'Accepted by MNR',
  SECTION_ACCEPTED_DESCRIPTION: 'These offers have been approved by the MNR Communications team. Publish them to make them live on the Perks Page.',
  SECTION_LIVE_TITLE: 'Live on Perks Page',
  SECTION_LIVE_DESCRIPTION: 'These offers are currently visible to all My NEXT Raise members on the Perks Page.',
  SECTION_REJECTED_TITLE: 'Needs Changes',
  SECTION_REJECTED_DESCRIPTION:
    'The MNR Communications team could not accept these as written. Edit an offer to send it back for review.',
  SECTION_ALL_TITLE: 'All Created Offers',
  SECTION_ALL_DESCRIPTION: 'All offers you have created — including drafts, inactive, and rejected offers.',
  SECTION_EMPTY: 'No offers here yet.',
  NO_EXPIRY: 'No expiry',
  /**
   * MYN-106 AC7. Reads zero on every offer until the member Perks page ships
   * (MYN-107) and starts recording clicks - the wording says what the number
   * counts so that zero does not read as "nobody is interested".
   */
  CTA_CLICKS: 'Claim Offer clicks',
  /** MYN-106 AC6: the reason MNR gave, shown to the partner. */
  REJECTION_REASON_LABEL: 'MNR said:',
  REJECTED_NEXT_STEP: 'Editing this offer returns it to Draft so you can send it back for review.',
  EDIT_AND_RESUBMIT: 'Edit offer',
  WITHDRAW: 'Withdraw',
  PUBLISH: '🌐 Publish to Perks Page',
  DEACTIVATE: 'Deactivate',
  LOAD_ERROR_TITLE: 'Could not load your offers',
  LOAD_ERROR_DESCRIPTION: 'Something went wrong loading your offers. Please try again.',
  WITHDRAW_SUCCESS: 'Offer withdrawn to Draft.',
  WITHDRAW_ERROR: 'Could not withdraw this offer. Please try again.',
  PUBLISH_SUCCESS: 'Offer published to the Perks Page.',
  PUBLISH_ERROR: 'Could not publish this offer. Please try again.',
  DEACTIVATE_SUCCESS: 'Offer deactivated.',
  DEACTIVATE_ERROR: 'Could not deactivate this offer. Please try again.',

  BACK_TO_PERKS: '← Back to Perks',
  CREATE_FORM_TITLE: 'Create Special Offer',
  CREATE_FORM_DESCRIPTION: 'Fill out the details below to submit a new offer for MNR review.',
  EDIT_FORM_TITLE: 'Edit Special Offer',
  EDIT_FORM_DESCRIPTION: 'Update your draft offer below.',
  NOT_FOUND_TITLE: 'Offer not found',
  NOT_FOUND_DESCRIPTION: 'This offer could not be found, or it no longer belongs to you.',
  SECTION_DETAILS: 'Offer Details',
  SECTION_VISIBILITY: 'Visibility & Timing',
  SECTION_NOTES: 'Notes for MNR Team',
  FIELD_LOGO: 'Logo',
  FIELD_LOGO_HINT: "Pulled from your Trusted Partner profile - update it there to change it here.",
  FIELD_TITLE: 'Perk Title',
  FIELD_SUBTITLE: 'Perk Subtitle',
  FIELD_COVER_PHOTO: 'Cover Photo',
  FIELD_COVER_PHOTO_HINT: 'PNG, JPG, or PDF. Images are cropped to 16:9; about 1200 × 675 px is best.',
  FIELD_COVER_PHOTO_UPLOAD: 'Upload Photo',
  FIELD_COVER_PHOTO_REPLACE: 'Replace Photo',
  FIELD_COVER_PHOTO_WRONG_TYPE: 'Please upload a PNG, JPG, or PDF file.',
  FIELD_COVER_PHOTO_TOO_LARGE: 'This file is too large. Please upload a smaller file.',
  FIELD_COVER_PHOTO_UPLOAD_FAILED: 'Could not upload the cover photo. Please try again.',
  FIELD_DESCRIPTION: 'Perk Description',
  FIELD_AVAILABLE_FROM: 'Available From',
  FIELD_EXPIRES_AT: 'Available Until',
  FIELD_CLAIM_LINK: 'Claim Link',
  FIELD_CLAIM_LINK_HINT: 'Where members go to claim this offer (https://...).',
  FIELD_CLAIM_EMAIL: 'Email',
  FIELD_CLAIM_EMAIL_HINT:
    'Shown to members as a Contact Us button that emails you. Add a claim link, an email, or both.',
  FIELD_INTERNAL_NOTES: 'Internal Notes',
  /** MYN-106 AC2: the live preview beside the create/edit form. */
  PREVIEW_HEADING: 'Preview',
  PREVIEW_HINT: 'This is how your offer will appear to members on the Perks Page.',
  PREVIEW_TITLE_PLACEHOLDER: 'Your perk title',
  PREVIEW_DESCRIPTION_PLACEHOLDER: 'Your perk description will appear here as you type.',
  PREVIEW_PARTNER_PLACEHOLDER: 'Your business',
  VALIDATION_SUMMARY_TITLE: 'Please fix the following before submitting:',
  DISCARD: 'Discard',
  SEND_FOR_REVIEW: 'Send for Review',
  SAVE_DRAFT: 'Save Draft',
  SAVING: 'Saving…',
  SUBMIT_SUCCESS: 'Offer created and submitted for review.',
  SUBMIT_ERROR: 'Could not submit this offer. Please try again.',
  SAVE_ERROR_FALLBACK: 'Could not save this offer. Please check the fields below and try again.',
} as const;
